import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { formatDateIT } from "@/lib/formatDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Coach modal to edit a single workout-log entry (sets/notes/date).
 *
 * The set list is locally editable: add/remove rows, change weight/reps.
 * `set_number` is normalized to 1..N when saving.
 */
export default function EditLogModal({ log, clientId, onClose, onSaved }) {
  const [sets, setSets] = useState([]);
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!log) return;
    setSets(
      (log.sets || []).map((s, i) => ({
        set_number: s.set_number ?? i + 1,
        weight_kg: s.weight_kg ?? "",
        reps: s.reps ?? "",
      }))
    );
    setNotes(log.notes || "");
    setDate(log.date || "");
  }, [log]);

  // Lock body scroll + ESC close.
  useEffect(() => {
    if (!log) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [log, onClose]);

  if (!log) return null;

  const updateSet = (idx, field, val) => {
    setSets((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));
  };

  const addSet = () => {
    setSets((prev) => [...prev, { set_number: prev.length + 1, weight_kg: "", reps: "" }]);
  };

  const removeSet = (idx) => {
    setSets((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      const payload = {
        sets: sets.map((s, i) => ({
          set_number: i + 1,
          weight_kg: s.weight_kg === "" || s.weight_kg == null ? null : Number(s.weight_kg),
          reps: s.reps === "" || s.reps == null ? null : Number(s.reps),
        })),
        notes,
        date,
      };
      await axios.put(`${API}/clients/${clientId}/logs/${log.id}`, payload);
      toast.success("Log aggiornato");
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("[EditLogModal] save failed", err);
      toast.error("Impossibile salvare le modifiche");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-log-title"
      data-testid="edit-log-modal"
    >
      <button
        type="button"
        aria-label="Chiudi"
        onClick={onClose}
        className="absolute inset-0 bg-black/75 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-md max-h-[90vh] bg-[#0F0F0F] border border-[#262626] rounded-2xl flex flex-col overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#1A1A1A] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-500">Modifica log</p>
            <h3
              id="edit-log-title"
              className="text-base font-bold tracking-tight text-white uppercase mt-0.5 truncate"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              {log.exercise_name}
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">{formatDateIT(log.date)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#1A1A1A] hover:bg-[#262626] text-zinc-400 hover:text-white flex items-center justify-center flex-shrink-0"
            aria-label="Chiudi"
            data-testid="edit-log-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {/* Date editor */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5 block">Data</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-[#171717] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#007AFF]"
              data-testid="edit-log-date"
            />
          </div>

          {/* Sets editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Serie</label>
              <button
                type="button"
                onClick={addSet}
                className="text-[11px] text-[#007AFF] hover:text-[#3395FF] flex items-center gap-1"
                data-testid="edit-log-add-set"
              >
                <Plus size={11} weight="bold" /> Aggiungi
              </button>
            </div>
            <div className="grid grid-cols-[24px_1fr_1fr_28px] gap-2 text-[10px] uppercase tracking-wider font-bold text-zinc-600 mb-1 px-1">
              <span>#</span>
              <span>Kg</span>
              <span>Reps</span>
              <span />
            </div>
            <div className="space-y-1.5">
              {sets.map((s, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[24px_1fr_1fr_28px] gap-2 items-center"
                  data-testid={`edit-log-set-row-${i}`}
                >
                  <span className="text-xs text-zinc-500 text-center">{i + 1}</span>
                  <input
                    type="number"
                    step="0.5"
                    inputMode="decimal"
                    value={s.weight_kg}
                    onChange={(e) => updateSet(i, "weight_kg", e.target.value)}
                    placeholder="—"
                    className="bg-[#171717] border border-[#262626] rounded-md px-2 py-1.5 text-sm text-[#007AFF] font-semibold outline-none focus:border-[#007AFF] text-center"
                    data-testid={`edit-log-set-kg-${i}`}
                  />
                  <input
                    type="number"
                    inputMode="numeric"
                    value={s.reps}
                    onChange={(e) => updateSet(i, "reps", e.target.value)}
                    placeholder="—"
                    className="bg-[#171717] border border-[#262626] rounded-md px-2 py-1.5 text-sm text-[#D4FF00] font-semibold outline-none focus:border-[#007AFF] text-center"
                    data-testid={`edit-log-set-reps-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeSet(i)}
                    className="w-7 h-7 rounded-md bg-[#1A1A1A] hover:bg-[#FF3B30]/20 hover:text-[#FF3B30] text-zinc-500 flex items-center justify-center"
                    aria-label="Rimuovi serie"
                    data-testid={`edit-log-remove-set-${i}`}
                  >
                    <Trash size={12} />
                  </button>
                </div>
              ))}
              {sets.length === 0 && (
                <p className="text-xs text-zinc-600 italic py-2">Nessuna serie. Aggiungine una.</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5 block">Note</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Note opzionali…"
              className="w-full bg-[#171717] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#007AFF] resize-y"
              data-testid="edit-log-notes"
            />
          </div>
        </div>

        <div className="px-5 py-3.5 border-t border-[#1A1A1A] flex items-center justify-end gap-2 bg-[#0A0A0A]">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-[#1A1A1A] border border-[#262626] text-zinc-300 hover:text-white text-sm font-medium disabled:opacity-50"
            data-testid="edit-log-cancel"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="edit-log-save"
          >
            <FloppyDisk size={14} weight="bold" /> Salva
          </button>
        </div>
      </div>
    </div>
  );
}
